import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, searchPatch, optionalSearch } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { AdmissionAvailabilityList } from "@/routes/_authenticated/_administration/settings/-components/forms/admission-availability-list";
import { FormRequestDialog } from "@/routes/_authenticated/_administration/settings/-components/forms/form-request-dialog";
import { FormRequestList } from "@/routes/_authenticated/_administration/settings/-components/forms/form-request-list";
import { useAdmissionFacility } from "@/routes/_authenticated/_administration/settings/-components/forms/use-admission-facility";

const LEVELS = ["required", "optional", "prn"] as const;

export const Route = createFileRoute("/_authenticated/_administration/settings/move-in/forms")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; level?: (typeof LEVELS)[number] } => ({
    q: optionalSearch(search.q),
    level: optionalOneOf(LEVELS, search.level),
  }),
  component: AdmissionFormAvailabilitySettings,
});

/** Which forms a new packet starts with, which staff can opt into, and which wait until needed;
 * packets already created keep what they have. */
function AdmissionFormAvailabilitySettings() {
  const facility = useAdmissionFacility();
  // Building admins hold this at their building, never company-wide, so ask about this one.
  const canManage = useCan("settings:manage", facility.facilityId);
  const [requesting, setRequesting] = useState(false);
  const { q, level } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => patch({ q: next }));

  const settings = useQuery({
    ...orpc.care.admissions.exhibitSettings.get.queryOptions({
      input: { facilityId: facility.facilityId },
    }),
    enabled: facility.facilityId !== "",
  });

  const levels: Record<string, string> = {};
  for (const row of settings.data?.availability ?? []) levels[row.exhibitKey] = row.availability;

  return (
    <>
      <RecordCard title="Forms" canWrite={canManage}>
        {settings.isError ? (
          <ReadFailed
            className="mb-3"
            message="This building’s own choices could not be loaded, so what is shown below is the standard set rather than yours."
            error={settings.error}
            retrying={settings.isFetching}
            onRetry={() => void settings.refetch()}
          />
        ) : null}
        <AdmissionAvailabilityList
          facilityId={facility.facilityId}
          levels={levels}
          canManage={canManage}
          isLoading={facility.facilityId === "" || settings.isPending}
          search={search}
          onSearch={setSearch}
          only={level ?? "all"}
          onOnly={(next) => patch({ level: next === "all" ? undefined : next })}
        />
      </RecordCard>

      <RecordCard
        title="Requested forms"
        canWrite={canManage && facility.facilityId !== ""}
        addLabel="Request form"
        onAdd={() => setRequesting(true)}
      >
        <FormRequestList facilityId={facility.facilityId} />
      </RecordCard>

      {facility.facilityId ? (
        <FormRequestDialog
          key={facility.facilityId}
          open={requesting}
          onOpenChange={setRequesting}
          facilityId={facility.facilityId}
        />
      ) : null}
    </>
  );
}
